/* Ardor Canvas Print Builder: 3D viewer of a canvas print hanging on a wall (three.js r147 classic build).
   Two styles: a stretched gallery wrap (a box) and a canvas panel set in a floating frame.
   Global: window.ArdorCanvas3D.create(hostDiv, opts) -> viewer or null.
   Needs global THREE + THREE.OrbitControls (load ardor-canvas-three.js, then ardor-canvas-three-orbit.js, before this file).
   Units: 1 scene unit = 1 inch. Everything is drawn at true size: nothing is made thicker or thinner than it is.

   viewer.set({ w, h, depth, edge })  size in inches (already swapped for orientation), frame depth,
                                      edge = 'mirror' | 'wrap' | 'black' | 'white'
   viewer.set({ w, h, depth, style: 'frame', frame })
                                      style = 'wrap' (the default: gallery wrap) | 'frame' (floating frame).
                                      For 'frame', w x h is the picture, depth is how deep the frame is and
                                      frame = 'pecan' | 'black' | 'white'. Optional gap and face (inches, 0.25 each):
                                      the gap between the picture and the frame, and how wide the frame is from the front.
   viewer.setFace('front', canvas)    the 2D drawing. mirror / black / white and the floating frame: the face only.
                                      wrap: the face plus a depth-wide band on every side.
   viewer.refresh()                   read the canvas again after it was redrawn
   viewer.view('home' | 'front' | 'left' | 'right'), viewer.zoom(1 | -1), viewer.resize(),
   viewer.snapshot(w, h), viewer.dispose()
   v2 (Oct 19, 2026, wall art; copy of ardor-canvas3d.js, unchanged): two more styles and a wood frame color.
   viewer.set({ w, h, depth, style: 'acrylic', acr, corner, holes, standoff })
                                      a clear acrylic panel depth thick (0.25 in), with rounded corners (corner = radius in
                                      inches), standing standoff inches off the wall on chrome stand offs (holes = [{ x, y, so }],
                                      inches from the top-left of the front; so = stand off). acr = 'white' | 'spot' | 'clear':
                                      the face picture may be see-through (its alpha), the panel edges are clear.
   viewer.set({ w, h, depth, style: 'roll' }) a flat sheet of canvas (depth is its thickness), white edges.
   frame = 'wood' for the framed print (style 'frame' with gap and face set by the builder). Sizes up to 1300 in. */
(function (root) {
  'use strict';

  var FOV = 28;
  var CENTER_Y = 57;       // centre of the print above the floor, a normal hanging height
  var AZ_MAX = 1.05;       // swing about 60 degrees left and right
  var EL_UP = 0.3;         // look from a little above
  var EL_DOWN = 0.14;      // and from a little below
  var FILL_SMALL = 0.56;   // share of the view an 8 x 10 takes in the home view
  var FILL_LARGE = 0.82;   // share of the view a 32 x 48 takes, so bigger prints look bigger
  var FILL_NEAR = 0.98;    // zoomed all the way in: the print just fits the view
  var FILL_FAR = 0.2;      // zoomed all the way out
  var AMBIENT_I = 0.5;     // room light
  var KEY_I = 0.5;         // window light from the upper left
  var KEY_DIR = [-0.5, 0.62, 0.6];
  var FILL_DIR = [0.6, 0.1, 0.79];
  var DEFAULT_BG = '#f4f3f0';
  var EDGE_BLACK = '#161616';
  // floating frame
  var FRAME_COLORS = { pecan: '#9A6842', black: '#1B1B1D', white: '#FAF9F6', wood: '#D9CCA7' };
  var FRAME_GAP = 0.25;    // inches between the picture and the frame
  var FRAME_FACE = 0.25;   // inches: how wide the frame is, seen from the front
  var FRAME_RECESS = 0.125; // the picture sits this far behind the front of the frame
  var FRAME_WELL = 0.75;   // how far the gap goes down behind the front of the frame
  var WELL_SHADE = 0.07;   // the inside of the gap is in shadow

  function hasThree() {
    var T = root.THREE;
    return !!(T && T.WebGLRenderer && T.OrbitControls && T.BufferGeometry && T.MeshLambertMaterial);
  }

  function create(host, opts) {
    try {
      if (!host || !hasThree()) return null;
      return build(host, opts || {});
    } catch (e) {
      return null;
    }
  }

  function unit(a) {
    var l = Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]) || 1;
    return [a[0] / l, a[1] / l, a[2] / l];
  }

  function inches(v, lo, hi) {
    var n = Number(v);
    return (v !== null && v !== '' && isFinite(n) && n >= lo && n <= hi) ? n : null;
  }

  function edgeName(v) {
    var s = String(v === undefined || v === null ? '' : v).toLowerCase();
    if (s.indexOf('mirror') === 0) return 'mirror';
    if (s.indexOf('wrap') >= 0) return 'wrap';
    if (s.indexOf('black') === 0) return 'black';
    if (s.indexOf('white') === 0) return 'white';
    return '';
  }

  function styleName(v) {
    var s = String(v === undefined || v === null ? '' : v).toLowerCase();
    if (s.indexOf('acryl') === 0) return 'acrylic';
    if (s.indexOf('roll') === 0) return 'roll';
    if (s.indexOf('frame') >= 0 || s.indexOf('float') === 0) return 'frame';
    if (s.indexOf('wrap') >= 0 || s.indexOf('gallery') === 0) return 'wrap';
    return '';
  }

  function frameName(v) {
    var s = String(v === undefined || v === null ? '' : v).toLowerCase();
    if (s.indexOf('pecan') === 0) return 'pecan';
    if (s.indexOf('wood') === 0) return 'wood';
    if (s.indexOf('black') === 0) return 'black';
    if (s.indexOf('white') === 0 || s.indexOf('off') === 0) return 'white';
    return '';
  }

  function wallColor(v) {
    if (typeof v !== 'string') return DEFAULT_BG;
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)) return v;
    if (/^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/i.test(v)) return v;
    return DEFAULT_BG;
  }

  function build(host, opts) {
    var THREE = root.THREE;
    var doc = host.ownerDocument || document;
    var win = doc.defaultView || root;
    var bg = wallColor(opts.background);

    /* ---------- canvas + GL context (probe first so three never logs an error) ---------- */
    var canvas = doc.createElement('canvas');
    canvas.setAttribute('aria-label', '3D preview of your canvas print on a wall. Drag to turn it.');
    canvas.style.cssText = 'position:absolute;top:0;left:0;right:0;bottom:0;inset:0;width:100%;height:100%;display:block;touch-action:none;outline:none;cursor:grab;';
    var attrs = {
      alpha: false, antialias: true, depth: true, stencil: false, premultipliedAlpha: true,
      preserveDrawingBuffer: false
    };
    var gl = null;
    try { gl = canvas.getContext('webgl2', attrs); } catch (e1) { gl = null; }
    if (!gl) { try { gl = canvas.getContext('webgl', attrs) || canvas.getContext('experimental-webgl', attrs); } catch (e2) { gl = null; } }
    if (!gl) return null;

    var renderer = new THREE.WebGLRenderer({
      canvas: canvas, context: gl, antialias: true, alpha: false, stencil: false, preserveDrawingBuffer: false
    });
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.NoToneMapping; // print colours stay exactly as drawn
    renderer.setClearColor(new THREE.Color(bg), 1); // the wall

    try {
      if (win.getComputedStyle && win.getComputedStyle(host).position === 'static') host.style.position = 'relative';
    } catch (e3) { /* ignore */ }
    host.appendChild(canvas);

    var maxAniso = 1;
    try { maxAniso = renderer.capabilities.getMaxAnisotropy() || 1; } catch (e4) { maxAniso = 1; }
    var canMip = !!renderer.capabilities.isWebGL2; // WebGL 1 cannot mipmap odd-sized pictures

    /* ---------- scene and light ---------- */
    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(FOV, 1, 2, 3000);
    var disposed = false;

    // Plain diffuse light that adds up to exactly 1.0 on the front of the print, so the picture
    // keeps the colours of the 2D drawing. The sides get less light, which is what shows the depth.
    var kd = unit(KEY_DIR), fd = unit(FILL_DIR);
    var fillI = Math.max(0, (1 - AMBIENT_I - KEY_I * kd[2]) / fd[2]);
    var amb = new THREE.AmbientLight(0xffffff, AMBIENT_I);
    var key = new THREE.DirectionalLight(0xffffff, KEY_I);
    key.position.set(kd[0], kd[1], kd[2]);
    var fill = new THREE.DirectionalLight(0xffffff, fillI);
    fill.position.set(fd[0], fd[1], fd[2]);
    scene.add(amb);
    scene.add(key);
    scene.add(fill);

    /* ---------- materials (live for the whole viewer) ---------- */
    function lin(hex) { var c = new THREE.Color(hex); c.convertSRGBToLinear(); return c; }
    var WHITE = lin('#ffffff'), BLACK = lin(EDGE_BLACK);

    // Any part of the drawing that is see-through shows the white canvas, not black.
    function whiteUnderPicture(shader) {
      shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', [
        '#ifdef USE_MAP',
        '  vec4 sampledDiffuseColor = texture2D( map, vUv );',
        '  sampledDiffuseColor = vec4( sampledDiffuseColor.rgb + vec3( 1.0 - sampledDiffuseColor.a ), 1.0 );',
        '  diffuseColor *= sampledDiffuseColor;',
        '#endif'
      ].join('\n'));
    }

    var frontMat = new THREE.MeshLambertMaterial({ color: WHITE.clone() });
    var sideMat = new THREE.MeshLambertMaterial({ color: WHITE.clone() });
    frontMat.onBeforeCompile = whiteUnderPicture;
    sideMat.onBeforeCompile = whiteUnderPicture;
    // floating frame: the frame itself, and the shaded inside of the gap around the picture
    var frameMat = new THREE.MeshLambertMaterial({ color: lin(FRAME_COLORS.pecan), side: THREE.DoubleSide });
    var wellMat = new THREE.MeshLambertMaterial({ color: lin(FRAME_COLORS.pecan).multiplyScalar(WELL_SHADE), side: THREE.DoubleSide });
    var shadowMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false, toneMapped: false });
    var haloMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false, toneMapped: false });
    // v2: acrylic print: the printed face (may be see-through), the clear edges and the chrome stand offs
    var acrFaceMat = new THREE.MeshLambertMaterial({ color: WHITE.clone(), transparent: true, premultipliedAlpha: true, depthWrite: false });
    var acrEdgeMat = new THREE.MeshLambertMaterial({ color: lin('#B9D8CF'), transparent: true, opacity: 0.6, depthWrite: false });
    var chromeMat = new THREE.MeshLambertMaterial({ color: lin('#C3C7CB') });

    var src = null;       // the builder's 2D canvas
    var tex = null;       // its texture
    var texW = 0, texH = 0;
    var shadowTex = null, haloTex = null;

    /* ---------- state ---------- */
    var state = { w: 16, h: 20, depth: 1.5, edge: 'mirror', band: null, style: 'wrap', frame: 'pecan', gap: FRAME_GAP, face: FRAME_FACE, acr: 'white', corner: 0, holes: '[]', standoff: 0 };
    var modelRoot = null;
    var geos = [];
    var bounds = { hw: 8, hh: 10, hz: 0.75 };
    var target = new THREE.Vector3(0, CENTER_Y, 0.75);
    var fitHome = 100;
    var size = { w: 0, h: 0 };

    var VIEWS = {
      home: { az: 0.5, el: 0.08 },
      front: { az: 0, el: 0 },
      left: { az: -0.92, el: 0.06 },
      right: { az: 0.92, el: 0.06 },
      side: { az: 0.92, el: 0.06 }
    };

    /* ---------- controls ---------- */
    var controls = new THREE.OrbitControls(camera, canvas);
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.12;
    controls.rotateSpeed = 0.8;
    controls.zoomSpeed = 0.9;
    controls.minAzimuthAngle = -AZ_MAX; // never behind the wall
    controls.maxAzimuthAngle = AZ_MAX;
    controls.minPolarAngle = Math.PI / 2 - EL_UP;
    controls.maxPolarAngle = Math.PI / 2 + EL_DOWN;
    controls.target.copy(target);

    var raf = 0;
    var tween = null;

    function now() { return (win.performance && win.performance.now) ? win.performance.now() : Date.now(); }

    function invalidate() {
      if (disposed || raf) return;
      raf = win.requestAnimationFrame(frame);
    }

    // A picture the browser will not let WebGL read must never stop the viewer: show a blank print instead.
    function draw() {
      try {
        renderer.render(scene, camera);
      } catch (e) {
        if (tex) {
          src = null;
          applyTexture();
          try { renderer.render(scene, camera); } catch (e2) { /* ignore */ }
        }
      }
    }

    function frame() {
      raf = 0;
      if (disposed) return;
      if (!size.w && !measure()) return; // the host has no size yet: wait for it
      if (tween) stepTween();
      controls.update(); // fires "change" (-> invalidate) while damping is still moving
      holdInView();
      draw();
    }

    function renderNow() {
      if (disposed) return;
      draw();
    }

    function onControlsChange() { invalidate(); }
    function onControlsStart() { tween = null; canvas.style.cursor = 'grabbing'; }
    function onControlsEnd() { canvas.style.cursor = 'grab'; }
    controls.addEventListener('change', onControlsChange);
    controls.addEventListener('start', onControlsStart);
    controls.addEventListener('end', onControlsEnd);

    /* ---------- camera helpers ---------- */
    function getSph() {
      var ox = camera.position.x - target.x, oy = camera.position.y - target.y, oz = camera.position.z - target.z;
      var r = Math.sqrt(ox * ox + oy * oy + oz * oz) || 1;
      return { az: Math.atan2(ox, oz), el: Math.asin(Math.max(-1, Math.min(1, oy / r))), r: r };
    }

    function setSph(az, el, r) {
      var ce = Math.cos(el);
      camera.position.set(target.x + r * ce * Math.sin(az), target.y + r * Math.sin(el), target.z + r * ce * Math.cos(az));
      camera.lookAt(target);
    }

    // Camera distance at which the print takes the given share of the view, for a view direction.
    function fitDistance(az, el, aspect, share) {
      var tv = Math.tan(FOV * Math.PI / 360), th = tv * (aspect || 1);
      var ce = Math.cos(el), se = Math.sin(el), sa = Math.sin(az), ca = Math.cos(az);
      var dx = ce * sa, dy = se, dz = ce * ca;
      var m = 1 / (share || 1);
      var best = 1, i, x, y, z, cx, cy, cz, d;
      for (i = 0; i < 8; i++) {
        x = (i & 1) ? bounds.hw : -bounds.hw;
        y = (i & 2) ? bounds.hh : -bounds.hh;
        z = (i & 4) ? bounds.hz : -bounds.hz;
        cx = x * ca - z * sa;
        cy = -x * se * sa + y * ce - z * se * ca;
        cz = x * dx + y * dy + z * dz;
        d = cz + Math.max(Math.abs(cx) * m / th, Math.abs(cy) * m / tv);
        if (d > best) best = d;
      }
      return best;
    }

    function aspectNow() { return (size.w > 0 && size.h > 0) ? size.w / size.h : 1.4; }

    // Small prints sit smaller in the view than large ones: a simple cue for how big the print is.
    function homeShare() {
      var t = (Math.max(state.w, state.h) - 10) / 38;
      t = Math.max(0, Math.min(1, t));
      return FILL_SMALL + (FILL_LARGE - FILL_SMALL) * t;
    }

    function viewDistance(v) { return fitDistance(v.az, v.el, aspectNow(), homeShare()); }

    // Closest the camera may come for a view direction: the whole print still fits the view.
    function nearFor(az, el) { return fitDistance(az, el, aspectNow(), FILL_NEAR); }

    function updateLimits() {
      var s = getSph();
      fitHome = viewDistance(VIEWS.home);
      controls.minDistance = nearFor(s.az, s.el);
      controls.maxDistance = Math.max(fitDistance(0, 0, aspectNow(), FILL_FAR), fitHome * 1.2);
    }

    function clampR(r, az, el) { return Math.max(nearFor(az, el), Math.min(controls.maxDistance, r)); }

    // The near limit depends on the angle (a print seen from the side comes closer on one edge),
    // so it follows the camera, and the camera is moved back when a turn would push the print out of view.
    function holdInView() {
      var s = getSph(), near = nearFor(s.az, s.el);
      controls.minDistance = near;
      if (!tween && s.r < near - 0.01) setSph(s.az, s.el, near);
    }

    function stepTween() {
      var t = (now() - tween.t0) / tween.ms;
      if (t >= 1) t = 1;
      var k = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      setSph(tween.a.az + (tween.b.az - tween.a.az) * k, tween.a.el + (tween.b.el - tween.a.el) * k, tween.a.r + (tween.b.r - tween.a.r) * k);
      if (t >= 1) tween = null; else invalidate();
    }

    function tweenTo(az, el, r, ms) {
      var a = getSph();
      var d = az - a.az;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      tween = { a: a, b: { az: a.az + d, el: el, r: r }, t0: now(), ms: ms || 520 };
      invalidate();
    }

    /* ---------- the print: a box on the wall ---------- */
    function track(g) { geos.push(g); return g; }

    // Front and four sides (the back is against the wall). Picture coordinates: u runs left to right,
    // v runs bottom to top. Each side is mapped so that the picture folds around the edge it touches.
    function boxGeometry(w, h, d) {
      var x0 = -w / 2, x1 = w / 2, y0 = CENTER_Y - h / 2, y1 = CENTER_Y + h / 2, z0 = 0, z1 = d;
      var wrap = state.edge === 'wrap';
      var band = wrap ? (state.band || d) : 0;
      // the face inside the picture (all of it, except for an image wrap, where the picture has a band around it)
      var u0 = wrap ? band / (w + 2 * band) : 0, u1 = 1 - u0;
      var v0 = wrap ? band / (h + 2 * band) : 0, v1 = 1 - v0;
      // how much of the picture one side shows
      var su = wrap ? Math.min(band, d) / (w + 2 * band) : Math.min(0.5, d / w);
      var sv = wrap ? Math.min(band, d) / (h + 2 * band) : Math.min(0.5, d / h);
      // picture coordinate where each side meets the wall: outward for a wrap, back inward for a mirror
      var uL = wrap ? u0 - su : u0 + su, uR = wrap ? u1 + su : u1 - su;
      var vB = wrap ? v0 - sv : v0 + sv, vT = wrap ? v1 + sv : v1 - sv;
      var pos = [], nor = [], uv = [], idx = [];
      function quad(a, b, c, e, n, ta, tb, tc, te) {
        var i = pos.length / 3;
        pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], e[0], e[1], e[2]);
        nor.push(n[0], n[1], n[2], n[0], n[1], n[2], n[0], n[1], n[2], n[0], n[1], n[2]);
        uv.push(ta[0], ta[1], tb[0], tb[1], tc[0], tc[1], te[0], te[1]);
        idx.push(i, i + 1, i + 2, i, i + 2, i + 3);
      }
      // front
      quad([x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], [0, 0, 1], [u0, v0], [u1, v0], [u1, v1], [u0, v1]);
      // right side
      quad([x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [1, 0, 0], [u1, v0], [uR, v0], [uR, v1], [u1, v1]);
      // left side
      quad([x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], [-1, 0, 0], [uL, v0], [u0, v0], [u0, v1], [uL, v1]);
      // top
      quad([x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0], [0, 1, 0], [u0, v1], [u1, v1], [u1, vT], [u0, vT]);
      // bottom
      quad([x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], [0, -1, 0], [u0, vB], [u1, vB], [u1, v0], [u0, v0]);
      var g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
      g.setIndex(idx);
      g.addGroup(0, 6, 0);   // front -> frontMat
      g.addGroup(6, 24, 1);  // sides -> sideMat
      return g;
    }

    // Floating frame: the picture is a flat panel, with a gap all around it, inside a frame that is `face` wide from
    // the front and `d` deep. w x h is the picture; the frame adds (gap + face) on every side. All in inches, true size.
    function frameGeometry(w, h, d, gap, face) {
      var m = gap + face;
      var px0 = -w / 2, px1 = w / 2, py0 = CENTER_Y - h / 2, py1 = CENTER_Y + h / 2;   // the picture
      var ix0 = px0 - gap, ix1 = px1 + gap, iy0 = py0 - gap, iy1 = py1 + gap;         // inside of the frame
      var ox0 = px0 - m, ox1 = px1 + m, oy0 = py0 - m, oy1 = py1 + m;                 // outside of the frame
      var zF = d;                                         // front of the frame
      var zW = Math.max(d * 0.2, d - FRAME_WELL);         // bottom of the gap
      var zP = Math.max(zW + 0.05, d - Math.min(FRAME_RECESS, d * 0.2)); // the picture
      var pos = [], nor = [], uv = [], idx = [];
      function quad(a, b, c, e, n, tex) {
        var i = pos.length / 3;
        pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], e[0], e[1], e[2]);
        nor.push(n[0], n[1], n[2], n[0], n[1], n[2], n[0], n[1], n[2], n[0], n[1], n[2]);
        if (tex) uv.push(0, 0, 1, 0, 1, 1, 0, 1); else uv.push(0, 0, 0, 0, 0, 0, 0, 0);
        idx.push(i, i + 1, i + 2, i, i + 2, i + 3);
      }
      // the four sides of a block, facing out (or facing in, for the walls of the gap)
      function sides(x0, x1, y0, y1, z0, z1, inward) {
        var s = inward ? -1 : 1;
        quad([x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [s, 0, 0]);
        quad([x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], [-s, 0, 0]);
        quad([x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0], [0, s, 0]);
        quad([x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], [0, -s, 0]);
      }
      // 1. the picture
      quad([px0, py0, zP], [px1, py0, zP], [px1, py1, zP], [px0, py1, zP], [0, 0, 1], true);
      // 2. the frame: its outside and its front
      sides(ox0, ox1, oy0, oy1, 0, zF, false);
      quad([ox0, oy0, zF], [ox1, oy0, zF], [ox1, iy0, zF], [ox0, iy0, zF], [0, 0, 1]); // bottom rail
      quad([ox0, iy1, zF], [ox1, iy1, zF], [ox1, oy1, zF], [ox0, oy1, zF], [0, 0, 1]); // top rail
      quad([ox0, iy0, zF], [ix0, iy0, zF], [ix0, iy1, zF], [ox0, iy1, zF], [0, 0, 1]); // left rail
      quad([ix1, iy0, zF], [ox1, iy0, zF], [ox1, iy1, zF], [ix1, iy1, zF], [0, 0, 1]); // right rail
      // 3. the gap: the inside walls of the frame, the bottom of the gap and the sides of the picture panel
      sides(ix0, ix1, iy0, iy1, zW, zF, true);
      quad([ix0, iy0, zW], [ix1, iy0, zW], [ix1, iy1, zW], [ix0, iy1, zW], [0, 0, 1]);
      sides(px0, px1, py0, py1, zW, zP, false);
      var g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
      g.setIndex(idx);
      g.addGroup(0, 6, 0);    // picture -> frontMat
      g.addGroup(6, 48, 1);   // frame (4 sides + 4 rails) -> frameMat
      g.addGroup(54, 54, 2);  // gap (4 walls + bottom + 4 panel sides) -> wellMat
      return g;
    }

    // v2: acrylic panel: a (rounded) rectangle t thick. Picture coordinates on the front come from x and y, so the
    // face texture lines up with the flat drawing; the back faces the wall and is never seen.
    function acrylicGeometry(w, h, t, r) {
      var x0 = -w / 2, y0 = CENTER_Y - h / 2, x1 = x0 + w, y1 = y0 + h;
      r = Math.max(0, Math.min(r || 0, w / 2, h / 2));
      var sh = new THREE.Shape();
      if (r > 0.001) {
        sh.moveTo(x0 + r, y0); sh.lineTo(x1 - r, y0); sh.absarc(x1 - r, y0 + r, r, -Math.PI / 2, 0, false);
        sh.lineTo(x1, y1 - r); sh.absarc(x1 - r, y1 - r, r, 0, Math.PI / 2, false);
        sh.lineTo(x0 + r, y1); sh.absarc(x0 + r, y1 - r, r, Math.PI / 2, Math.PI, false);
        sh.lineTo(x0, y0 + r); sh.absarc(x0 + r, y0 + r, r, Math.PI, Math.PI * 1.5, false);
      } else { sh.moveTo(x0, y0); sh.lineTo(x1, y0); sh.lineTo(x1, y1); sh.lineTo(x0, y1); sh.lineTo(x0, y0); }
      var g = new THREE.ExtrudeGeometry(sh, { depth: t, bevelEnabled: false, curveSegments: 10 });
      var pos = g.attributes.position, uv = g.attributes.uv;
      for (var i = 0; i < pos.count; i++) uv.setXY(i, (pos.getX(i) - x0) / w, (pos.getY(i) - y0) / h);
      uv.needsUpdate = true;
      return g;
    }

    // A canvas the size of the print plus a margin, in which soft shadows of the print are drawn.
    // The black rectangle itself is drawn off the canvas, so only its blurred shadow lands on it.
    function shadowCanvas(w, h, pad, ppi) {
      var c = doc.createElement('canvas');
      c.width = Math.max(8, Math.round((w + 2 * pad) * ppi));
      c.height = Math.max(8, Math.round((h + 2 * pad) * ppi));
      var g = c.getContext('2d');
      var far = c.width * 3;
      g.fillStyle = '#000';
      return {
        canvas: c,
        add: function (blur, alpha, ox, oy) { // all in inches, except alpha
          g.shadowColor = 'rgba(0,0,0,' + alpha + ')';
          g.shadowBlur = blur * ppi;
          g.shadowOffsetX = far + ox * ppi;
          g.shadowOffsetY = oy * ppi;
          g.fillRect(pad * ppi - far, pad * ppi, w * ppi, h * ppi);
        }
      };
    }

    function shadowTexture(c) {
      var t = new THREE.CanvasTexture(c);
      t.generateMipmaps = false;
      t.minFilter = THREE.LinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      return t;
    }

    // The shadow the print throws on the wall, so the depth of the frame reads.
    function makeShadowTexture(w, h, d, pad) {
      var s = shadowCanvas(w, h, pad, Math.min(10, 512 / (Math.max(w, h) + 2 * pad)));
      s.add(1.7 * d, 0.24, 0.8 * d, 1.1 * d); // down and to the right: the light comes from the upper left
      s.add(0.67 * d, 0.28, 0, 0);            // all around, where the frame meets the wall
      s.add(0.14 * d, 0.2, 0, 0);             // a fine line right at the frame
      return shadowTexture(s.canvas);
    }

    // Very soft, wide shading of the wall around the print, so a white print still stands out on a white wall.
    function makeHaloTexture(w, h, pad) {
      var m = Math.max(w, h);
      var s = shadowCanvas(w, h, pad, 96 / (m + 2 * pad));
      s.add(0.8 * m, 0.085, 0, 0);
      return shadowTexture(s.canvas);
    }

    function clearModel() {
      var i;
      if (modelRoot) scene.remove(modelRoot);
      for (i = 0; i < geos.length; i++) geos[i].dispose();
      geos = [];
      modelRoot = null;
      if (shadowTex) { shadowTex.dispose(); shadowTex = null; }
      if (haloTex) { haloTex.dispose(); haloTex = null; }
      shadowMat.map = null;
      haloMat.map = null;
    }

    function wallLayer(map, mat, w, h, pad, z, order) {
      mat.map = map;
      mat.needsUpdate = true;
      var m = new THREE.Mesh(track(new THREE.PlaneGeometry(w + 2 * pad, h + 2 * pad)), mat);
      m.position.set(0, CENTER_Y, z);
      m.renderOrder = order;
      return m;
    }

    function rebuild() {
      clearModel();
      var w = state.w, h = state.h, d = state.depth;
      var framed = state.style === 'frame';
      var grp = new THREE.Group();

      if (state.style === 'acrylic') {
        // v2: the clear panel, off the wall on stand offs (spacer behind it, cap in front), or flat on the wall
        var off = state.standoff || 0, t = d, holes = [];
        try { holes = JSON.parse(state.holes) || []; } catch (eh) { holes = []; }
        var panel = new THREE.Mesh(track(acrylicGeometry(w, h, t, state.corner)), [acrFaceMat, acrEdgeMat]);
        panel.position.z = off; panel.renderOrder = 3;
        grp.add(panel);
        holes.forEach(function (hh) {
          if (!hh || !hh.so) return;
          var x = -w / 2 + hh.x, y = CENTER_Y + h / 2 - hh.y;
          if (off > 0) { var sp = new THREE.Mesh(track(new THREE.CylinderGeometry(0.5, 0.5, off, 28)), chromeMat); sp.rotation.x = Math.PI / 2; sp.position.set(x, y, off / 2); grp.add(sp); }
          var cap = new THREE.Mesh(track(new THREE.CylinderGeometry(0.5, 0.5, 0.375, 28)), chromeMat); cap.rotation.x = Math.PI / 2; cap.position.set(x, y, off + t + 0.1875); grp.add(cap);
        });
        d = off + t + (holes.some(function (hh) { return hh && hh.so; }) ? 0.375 : 0);
      } else if (state.style === 'roll') {
        state.edge = 'white'; // a flat sheet: thin white edges
        grp.add(new THREE.Mesh(track(boxGeometry(w, h, d)), [frontMat, sideMat]));
      } else if (framed) {
        var fc = lin(FRAME_COLORS[state.frame] || FRAME_COLORS.pecan);
        frameMat.color.copy(fc);
        wellMat.color.copy(fc).multiplyScalar(WELL_SHADE);
        grp.add(new THREE.Mesh(track(frameGeometry(w, h, d, state.gap, state.face)), [frontMat, frameMat, wellMat]));
        // from here on w x h is the outside of the frame: that is what hangs on the wall and throws the shadow
        w += 2 * (state.gap + state.face);
        h += 2 * (state.gap + state.face);
      } else {
        grp.add(new THREE.Mesh(track(boxGeometry(w, h, d)), [frontMat, sideMat]));
      }
      var pad = Math.max(8, d * 5.5);
      var haloPad = Math.max(w, h) * 1.3;

      haloTex = makeHaloTexture(w, h, haloPad);
      grp.add(wallLayer(haloTex, haloMat, w, h, haloPad, 0.01, 1));
      shadowTex = makeShadowTexture(w, h, d, pad);
      grp.add(wallLayer(shadowTex, shadowMat, w, h, pad, 0.02, 2));

      modelRoot = grp;
      scene.add(grp);
      bounds = { hw: w / 2, hh: h / 2, hz: d / 2 };
      target.set(0, CENTER_Y, d / 2);
      bindTexture();
    }

    /* ---------- the picture ---------- */
    function bindTexture() {
      var onSides = (tex && (state.edge === 'mirror' || state.edge === 'wrap')) ? tex : null;
      if (frontMat.map !== tex) { frontMat.map = tex; frontMat.needsUpdate = true; }
      if (acrFaceMat.map !== tex) { acrFaceMat.map = tex; acrFaceMat.needsUpdate = true; }
      if (sideMat.map !== onSides) { sideMat.map = onSides; sideMat.needsUpdate = true; }
      sideMat.color.copy(state.edge === 'black' ? BLACK : WHITE);
    }

    function applyTexture() {
      if (tex) { tex.dispose(); tex = null; }
      texW = 0; texH = 0;
      if (src && src.width > 0 && src.height > 0) {
        var t = new THREE.CanvasTexture(src);
        t.encoding = THREE.sRGBEncoding; // same colours as the 2D drawing
        t.premultiplyAlpha = true;       // goes with whiteUnderPicture
        t.anisotropy = maxAniso;         // stays sharp when seen at an angle
        t.generateMipmaps = canMip;
        t.minFilter = canMip ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
        t.magFilter = THREE.LinearFilter;
        t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
        t.needsUpdate = true;
        tex = t;
        texW = src.width; texH = src.height;
      }
      bindTexture();
    }

    /* ---------- sizing ---------- */
    function measure() {
      var w = host.clientWidth | 0, h = host.clientHeight | 0;
      if (w < 2 || h < 2) return false;
      var pr = Math.min(2, Math.max(1, win.devicePixelRatio || 1));
      if (w === size.w && h === size.h && pr === renderer.getPixelRatio()) return false;
      var oldFit = fitHome, had = size.w > 0;
      size.w = w; size.h = h;
      renderer.setPixelRatio(pr);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      updateLimits();
      var s = getSph();
      if (had && !tween) setSph(s.az, s.el, clampR(s.r * fitHome / oldFit, s.az, s.el));
      else if (!had) setSph(VIEWS.home.az, VIEWS.home.el, fitHome);
      else if (tween) tween.b.r = tween.b.r * fitHome / oldFit;
      controls.update();
      return true;
    }

    /* ---------- public API ---------- */
    var api = {
      set: function (o) {
        if (disposed || !o) return;
        var changed = false, v;
        v = inches(o.w, 2, 1300);
        if (v !== null && v !== state.w) { state.w = v; changed = true; }
        v = inches(o.h, 2, 1300);
        if (v !== null && v !== state.h) { state.h = v; changed = true; }
        v = inches(o.depth, 0.02, 4);
        if (v !== null && v !== state.depth) { state.depth = v; changed = true; }
        v = edgeName(o.edge);
        if (v && v !== state.edge) { state.edge = v; changed = true; }
        v = styleName(o.style);
        if (v && v !== state.style) { state.style = v; changed = true; }
        v = frameName(o.frame);
        if (v && v !== state.frame) { state.frame = v; changed = true; }
        v = inches(o.gap, 0.05, 3);
        if (v !== null && v !== state.gap) { state.gap = v; changed = true; }
        v = inches(o.face, 0.05, 6);
        if (v !== null && v !== state.face) { state.face = v; changed = true; }
        if (o.acr !== undefined && String(o.acr) !== state.acr) { state.acr = String(o.acr); changed = true; }
        v = inches(o.corner, 0, 3);
        if (v !== null && v !== state.corner) { state.corner = v; changed = true; }
        v = inches(o.standoff, 0, 4);
        if (v !== null && v !== state.standoff) { state.standoff = v; changed = true; }
        if (o.holes !== undefined) { var hs = JSON.stringify(o.holes || []); if (hs !== state.holes) { state.holes = hs; changed = true; } }
        if (o.band !== undefined) {
          // optional: width of the band around the face in an image-wrap picture, when it is not the frame depth
          v = inches(o.band, 0.1, 8);
          if (v !== state.band) { state.band = v; changed = true; }
        }
        if (!changed && modelRoot) return;
        var oldFit = fitHome, s = getSph();
        rebuild();
        controls.target.copy(target);
        updateLimits();
        if (tween) tween.b.r = tween.b.r * fitHome / oldFit;
        setSph(s.az, s.el, clampR(s.r * fitHome / oldFit, s.az, s.el));
        controls.minDistance = nearFor(s.az, s.el);
        controls.update();
        invalidate();
      },
      setFace: function (face, cv) {
        if (disposed || face !== 'front') return; // a canvas print has one printed face
        src = cv || null;
        applyTexture();
        invalidate();
      },
      refresh: function () {
        if (disposed) return;
        if (src && (src.width !== texW || src.height !== texH)) applyTexture(); // the drawing changed size
        else if (tex) tex.needsUpdate = true;
        invalidate();
      },
      view: function (name) {
        if (disposed) return;
        var v = VIEWS[name] || VIEWS.home;
        tweenTo(v.az, v.el, viewDistance(v), 560);
      },
      zoom: function (dir) {
        if (disposed) return;
        var s = tween ? tween.b : getSph();
        var r = clampR(s.r * Math.pow(0.8, dir > 0 ? 1 : -1), s.az, s.el);
        tweenTo(s.az, s.el, r, 240);
      },
      resize: function () {
        if (disposed) return;
        if (measure()) { raf && win.cancelAnimationFrame(raf); raf = 0; renderNow(); if (tween) invalidate(); }
      },
      snapshot: function (w, h, viewName) {
        if (disposed) return null;
        var out = null;
        var oldPR = renderer.getPixelRatio();
        var oldSize = renderer.getSize(new THREE.Vector2());
        var oldAspect = camera.aspect;
        var oldPos = camera.position.clone();
        try {
          var cap = Math.min(4096, renderer.capabilities.maxTextureSize || 4096);
          w = Math.max(16, Math.min(cap, Math.round(w || size.w || 800)));
          h = Math.max(16, Math.min(cap, Math.round(h || size.h || 600)));
          var s = getSph();
          var k = fitDistance(s.az, s.el, w / h, 1) / fitDistance(s.az, s.el, oldAspect || 1, 1);
          renderer.setPixelRatio(1);
          renderer.setSize(w, h, false);
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          var sv = (typeof viewName === 'string' && VIEWS.hasOwnProperty(viewName)) ? VIEWS[viewName] : null; // proof sheet (ardor-proofsheet.js): a named view, fitted to the picture
if (sv) setSph(sv.az, sv.el, fitDistance(sv.az, sv.el, w / h, homeShare())); else setSph(s.az, s.el, s.r * k);
          renderer.render(scene, camera);
          out = doc.createElement('canvas');
          out.width = w; out.height = h;
          var ctx = out.getContext('2d');
          ctx.fillStyle = bg;
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(canvas, 0, 0, w, h);
        } catch (e) {
          out = null;
        }
        try {
          renderer.setPixelRatio(oldPR);
          renderer.setSize(oldSize.x, oldSize.y, false);
          camera.aspect = oldAspect;
          camera.updateProjectionMatrix();
          camera.position.copy(oldPos);
          camera.lookAt(target);
          renderer.render(scene, camera);
        } catch (e2) { /* ignore */ }
        return out;
      },
      dispose: function () {
        if (disposed) return;
        disposed = true;
        if (raf) win.cancelAnimationFrame(raf);
        raf = 0; tween = null;
        try { if (ro) ro.disconnect(); } catch (e) { /* ignore */ }
        win.removeEventListener('resize', onWinResize);
        canvas.removeEventListener('webglcontextlost', onLost, false);
        canvas.removeEventListener('webglcontextrestored', onRestored, false);
        controls.removeEventListener('change', onControlsChange);
        controls.removeEventListener('start', onControlsStart);
        controls.removeEventListener('end', onControlsEnd);
        controls.dispose();
        clearModel();
        var list = [frontMat, sideMat, frameMat, wellMat, shadowMat, haloMat, acrFaceMat, acrEdgeMat, chromeMat, tex];
        for (var i = 0; i < list.length; i++) { try { if (list[i]) list[i].dispose(); } catch (e3) { /* ignore */ } }
        tex = null; src = null;
        try { renderer.dispose(); renderer.forceContextLoss(); } catch (e4) { /* ignore */ }
        if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      }
    };

    function onWinResize() { api.resize(); }
    function onLost(e) { e.preventDefault(); }
    function onRestored() { invalidate(); }
    canvas.addEventListener('webglcontextlost', onLost, false);
    canvas.addEventListener('webglcontextrestored', onRestored, false);

    var ro = null;
    if (win.ResizeObserver) {
      ro = new win.ResizeObserver(function () { api.resize(); });
      ro.observe(host);
    }
    win.addEventListener('resize', onWinResize);

    rebuild();
    controls.target.copy(target);
    setSph(VIEWS.home.az, VIEWS.home.el, fitHome);
    updateLimits();
    setSph(VIEWS.home.az, VIEWS.home.el, fitHome);
    if (!measure()) controls.update();
    invalidate();

    return api;
  }

  root.ArdorCanvas3D = { create: create };
})(typeof window !== 'undefined' ? window : this);
